import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ArrowLeft, Home, Loader2, QrCode } from "lucide-react";
import { fetchVoucherByCode, setQrCode, clearVoucher } from "@/redux/slices/qrSlice";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { assignVoucherdata } from "@/services/customer";
import { getT } from "@/i18n/translations";


export default function ScanPage() {
  const t = getT();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const token = useSelector((state) => state.auth.token);
  const { code: qrCode, voucher, loading: voucherLoading } = useSelector((state) => state.qr);
  const { data: customer } = useSelector((state) => state.customer);

  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lastProcessedCode = useRef(null);

  const [manualCode, setManualCode] = useState("");
  const [cameraError, setCameraError] = useState(false);
  const [scanning, setScanning] = useState(false);

  const [status, setStatus] = useState("");
  const [voucherValue, setVoucherValue] = useState(null);
  const [message, setMessage] = useState("");
  const [resultVisible, setResultVisible] = useState(false);

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
      startQrScan(stream);
    } catch {
      setCameraError(true);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const startQrScan = (stream) => {
    if (!("BarcodeDetector" in window)) return;
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });
    const video = videoRef.current;

    const scan = async () => {
      if (!video || video.readyState < 2) { requestAnimationFrame(scan); return; }
      try {
        const barcodes = await detector.detect(video);
        if (barcodes.length > 0) {
          const code = barcodes[0].rawValue;
          if (code && code !== lastProcessedCode.current) {
            stopCamera();
            submitCode(code);
            return;
          }
        }
      } catch { }
      requestAnimationFrame(scan);
    };
    requestAnimationFrame(scan);
  };

  const submitCode = (code) => {
    let c = code.trim();
    try {
      const url = new URL(c);
      const parts = url.pathname.split("/").filter(Boolean);
      if (parts.length > 0) c = parts[parts.length - 1];
    } catch { }
    c = c.toUpperCase();
    if (!c) return;
    stopCamera();
    localStorage.setItem("qrCode", c);
    dispatch(clearVoucher());
    dispatch(setQrCode(null));
    requestAnimationFrame(() => {
      dispatch(setQrCode(c));
      navigate("/home");
    });
  };

  useEffect(() => {
    if (!voucher || !qrCode) return;
    if (lastProcessedCode.current === qrCode) return;
    lastProcessedCode.current = qrCode;
    setScanning(false);

    const data = voucher?.data?.voucher;

    if (!data) { setStatus("error"); setMessage(t.scan_errInvalid); setResultVisible(true); return; }
    if (data?.voucherUsed === true) { setStatus("error"); setMessage(t.scan_errUsed); setResultVisible(true); return; }
    if (data?.isAssigned === true) { setStatus("error"); setMessage(t.scan_errAssigned); setResultVisible(true); return; }
    if (new Date(data?.expiryDate) < new Date()) { setStatus("error"); setMessage(t.scan_errExpired); setResultVisible(true); return; }

    const value = data?.vouchervalue ?? 0;
    setVoucherValue(value);

    if (value > 0) {
      assignVoucherdata(token, {
        client: "MER000479",
        voucherTemplate: data?.voucherTemplateCode,
        voucher: data?.code,
        Value: value,
        CustomerCode: localStorage.getItem("customercode"),
      }).then(() => {
        dispatch(fetchCustomerData(localStorage.getItem("customercode")));
      });
    }

    setStatus("success");
    setMessage(t.scan_errSuccess);
    setResultVisible(true);
  }, [voucher, qrCode]);

  const handleScanAnother = () => {
    setResultVisible(false);
    setStatus("");
    setMessage("");
    setVoucherValue(null);
    setManualCode("");
    dispatch(setQrCode(null));
    dispatch(clearVoucher());
    localStorage.removeItem("qrCode");
    lastProcessedCode.current = null;
    startCamera();
  };

  return (
    <div
  className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: '#f0f4f0',
        backgroundImage: 'radial-gradient(circle, #c8d8c8 1px, transparent 1px)',
        backgroundSize: '22px 22px',
      }}
    >
      {(voucherLoading || scanning) && (
        <div className="fixed inset-0 bg-black/20 flex justify-center items-center z-50 backdrop-blur-sm">
          <Loader2 className="animate-spin h-12 w-12 text-[#2d5a2d]" />
        </div>
      )}

   <div className="w-full max-w-md min-h-screen flex flex-col">

        {/* Header */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => { stopCamera(); navigate(-1); }}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <ArrowLeft className="w-5 h-5 text-[#1a2e1a]" />
          </button>
          <p className="text-[15px] font-semibold text-[#1a2e1a]">{t.scan_pageTitle}</p>
          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm"
          >
            <Home className="w-5 h-5 text-[#1a2e1a]" />
          </button>
        </div>

      <div className="px-4 pb-8 space-y-3 flex-1">

          {!resultVisible ? (
            <>
              {/* Camera viewfinder */}
              <div className="bg-black rounded-3xl overflow-hidden mx-1 relative" style={{ aspectRatio: '1/1' }}>
                {!cameraError ? (
                  <>
                    <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-52 h-52 relative">
                        {[
                          "top-0 left-0 border-t-[3px] border-l-[3px] rounded-tl-xl",
                          "top-0 right-0 border-t-[3px] border-r-[3px] rounded-tr-xl",
                          "bottom-0 left-0 border-b-[3px] border-l-[3px] rounded-bl-xl",
                          "bottom-0 right-0 border-b-[3px] border-r-[3px] rounded-br-xl",
                        ].map((cls, i) => (
                          <div key={i} className={`absolute w-8 h-8 border-[#c8d400] ${cls}`} />
                        ))}
                        <div className="absolute inset-x-0 top-0 h-0.5 bg-[#c8d400] opacity-80 animate-bounce" style={{ animationDuration: '2s' }} />
                      </div>
                    </div>
                    <p className="absolute bottom-4 left-0 right-0 text-center text-[12px] text-white/70">
                      {t.scan_pointCamera}
                    </p>
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                    <QrCode size={48} className="text-white/40" />
                    <p className="text-[13px] text-white/60 text-center px-6">{t.scan_cameraUnavailable}</p>
                  </div>
                )}
              </div>

              {/* Manual entry */}
              <div className="mx-1">
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest text-center mb-3">
                  {t.scan_orEnterManually}
                </p>
                <div className="flex items-center border-[1.5px] border-[#e0e8e0] rounded-2xl px-4 py-3.5 gap-3 bg-white focus-within:border-[#3a6b3a] transition-colors mb-3">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#b0c4b0" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 5v2M3 19v-2M3 12h1M21 5v2M21 19v-2M21 12h-1M7 5v14M10 5v14M14 5v14M17 5v14" />
                  </svg>
                  <input
                    value={manualCode}
                    onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && submitCode(manualCode)}
                    placeholder={t.scan_placeholder}
                    className="flex-1 bg-transparent outline-none text-[18px] font-bold text-[#1a2e1a] tracking-widest placeholder:text-[#d0dcd0] placeholder:font-bold"
                  />
                </div>
                <button
                  onClick={() => submitCode(manualCode)}
                  disabled={!manualCode.trim()}
                  className="w-full py-4 rounded-2xl text-[15px] font-bold text-white flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ backgroundColor: '#2d5a2d' }}
                >
                  {t.scan_submitCode}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </>
          ) : (
            /* ── RESULT ── */
            <div className="bg-white rounded-2xl p-6 shadow-sm mx-1 text-center">

              {status === "success" ? (
                <>
                  {voucherValue > 0 ? (
                    <>
                      <h2 className="text-[22px] font-bold text-[#1a2e1a] mb-1">{t.scan_congrats}</h2>
                      <p className="text-[14px] text-gray-500 mb-0.5">{t.scan_youEarned}</p>
                      <p className="text-[14px] text-gray-500 mb-3">{t.scan_cashbackRewards}</p>
                      <p className="text-[48px] font-bold mb-6" style={{ color: '#2d5a2d' }}>₹{voucherValue}</p>

                      <button
                        onClick={() => { stopCamera(); navigate("/redeem-cash"); }}
                        className="w-full text-white py-3.5 rounded-xl font-semibold mb-3 flex items-center justify-center gap-2 active:scale-[0.98] transition-transform"
                        style={{ backgroundColor: "#2d5a2d" }}
                      >
                        {t.scan_viewEarnings}
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>

                      <button
                        onClick={handleScanAnother}
                        className="w-full py-3.5 rounded-xl font-semibold mb-3 text-[#1a2e1a] border-[1.5px] border-[#e0e8e0] active:scale-[0.98] transition-transform text-[14px]"
                      >
                        {t.scan_scanAnother}
                      </button>

                      <button
                        onClick={() => { stopCamera(); navigate("/slogan"); }}
                        className="w-full py-3.5 rounded-xl font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform text-[14px]"
                        style={{ backgroundColor: '#c8d400', color: '#1a2e00' }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="#1a2e00">
                          <path d="M6 2h12v3h3v3c0 3.31-2.69 6-6 6h-1v2h3v2H7v-2h3v-2H9c-3.31 0-6-2.69-6-6V5h3V2zm0 5H4v1c0 2.21 1.79 4 4 4V7zm12 0v5c2.21 0 4-1.79 4-4V7h-2z" />
                        </svg>
                        {t.scan_grandPrizeBtn}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="w-16 h-16 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-4 text-2xl">✔</div>
                      <h2 className="text-[16px] font-bold mb-2 text-[#1a2e1a]">{t.scan_voucherVerified}</h2>
                      <div className="bg-gray-50 border rounded-xl p-4 text-left mb-4">
                        {customer?.customer?.slogan ? (
                          <>
                            <p className="text-[14px] font-semibold mb-1 text-[#1a2e1a]">{t.scan_contestSubmitted}</p>
                            <p className="text-sm text-gray-500 leading-relaxed">{t.scan_contestSubmittedMsg}</p>
                          </>
                        ) : (
                          <>
                            <p className="text-[14px] font-semibold mb-1 text-[#1a2e1a]">{t.scan_contestNotSubmitted}</p>
                            <p className="text-sm text-gray-500 mb-3 leading-relaxed">{t.scan_contestNotSubmittedMsg}</p>
                            <button
                              onClick={() => { stopCamera(); navigate("/slogan"); }}
                              className="w-full text-white py-3 rounded-xl text-sm font-semibold active:scale-[0.98]"
                              style={{ backgroundColor: "#2d5a2d" }}
                            >
                              {t.scan_submitSlogan}
                            </button>
                          </>
                        )}
                      </div>
                      <button onClick={handleScanAnother} className="w-full py-3 rounded-xl text-[13px] font-semibold border-[1.5px] border-[#e0e8e0] text-[#1a2e1a]">
                        {t.scan_scanAnother}
                      </button>
                    </>
                  )}
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4 text-2xl text-red-500">✖</div>
                  <h2 className="text-[16px] font-bold mb-2 text-[#1a2e1a]">{t.scan_verificationFailed}</h2>
                  <p className="text-sm text-gray-500 mb-5">{message}</p>
                  <button
                    onClick={handleScanAnother}
                    className="w-full text-white py-3 rounded-xl font-semibold active:scale-[0.98]"
                    style={{ backgroundColor: "#2d5a2d" }}
                  >
                    {t.scan_tryAgain}
                  </button>
                </>
              )}
            </div>
          )}

        

        </div>
         <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Gencrest
          </p>
        </div>
      </div>
    </div>
  );
}